import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, Camera, Trash2 } from "lucide-react";
import { colors, type, spacing, radius, ALLERGEN_LABELS, TAG_LABELS } from "@/theme";
import { api, mediaUrl } from "@/lib/api";
import { useRequireAuth } from "@/lib/useRequireAuth";

function toCents(s) { const n = parseFloat((s || "").replace(",", ".")); return isNaN(n) ? 0 : Math.round(n * 100); }
function euro(c) { return (c / 100).toFixed(2).replace(".", ","); }

export default function AdminItem() {
  const navigate = useNavigate();
  const { user } = useRequireAuth();
  const [params] = useSearchParams();
  const id = params.get("id") || undefined;
  const category_id = params.get("category_id") || undefined;
  const editing = !!id;
  const fileRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cenaBandId, setCenaBandId] = useState(null);
  const [catId, setCatId] = useState(category_id);

  const [nome, setNome] = useState("");
  const [descrizione, setDescrizione] = useState("");
  const [prezzo, setPrezzo] = useState("");
  const [cena, setCena] = useState("");
  const [allergeni, setAllergeni] = useState(new Set());
  const [tag, setTag] = useState(new Set());
  const [tempo, setTempo] = useState("15");
  const [disponibile, setDisponibile] = useState(true);
  const [foto, setFoto] = useState({ foto_url: null, foto_thumb_url: null, foto_blurhash: null });

  useEffect(() => {
    if (!user) return;
    (async () => {
      const d = await api.adminMenuFull();
      const cenaBand = d.price_bands.find((b) => b.nome.toLowerCase() === "cena");
      setCenaBandId(cenaBand?.id || null);
      if (editing) {
        let found = null;
        for (const c of d.categorie) { const it = c.items.find((x) => x.id === id); if (it) { found = it; setCatId(c.id); break; } }
        if (found) {
          setNome(found.nome); setDescrizione(found.descrizione || "");
          setPrezzo(euro(found.prezzo_cent));
          const pf = (found.prezzi_fascia || []).find((p) => p.price_band_id === cenaBand?.id);
          if (pf) setCena(euro(pf.prezzo_cent));
          setAllergeni(new Set(found.allergeni || []));
          setTag(new Set(found.tag || []));
          setTempo(String(found.tempo_prep_min || 15));
          setDisponibile(found.disponibile);
          setFoto({ foto_url: found.foto_url, foto_thumb_url: found.foto_thumb_url, foto_blurhash: found.foto_blurhash });
        }
      }
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, [user, id]);

  if (!user) return null;

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { const out = await api.uploadPhoto(file); setFoto(out); } catch {} finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => {
    if (!nome.trim() || !catId) return;
    setSaving(true);
    try {
      const prezzi_fascia = cena.trim() && cenaBandId ? [{ price_band_id: cenaBandId, prezzo_cent: toCents(cena) }] : [];
      const body = { nome: nome.trim(), descrizione: descrizione.trim(), prezzo_cent: toCents(prezzo), prezzi_fascia, allergeni: Array.from(allergeni), tag: Array.from(tag), tempo_prep_min: parseInt(tempo) || 15, foto_url: foto.foto_url, foto_thumb_url: foto.foto_thumb_url, foto_blurhash: foto.foto_blurhash };
      if (editing) await api.updateItem(id, { ...body, disponibile });
      else { body.category_id = catId; await api.createItem(body); }
      navigate(-1);
    } finally { setSaving(false); }
  };

  const del = async () => { if (editing) { await api.deleteItem(id); navigate(-1); } };
  const toggleSet = (setter, val) => setter((p) => { const n = new Set(p); n.has(val) ? n.delete(val) : n.add(val); return n; });

  if (loading) return <div style={{ minHeight: "100vh", background: colors.bg, display: "flex", alignItems: "center", justifyContent: "center", ...type.sans, color: colors.accent }}>Caricamento…</div>;

  return (
    <div style={{ minHeight: "100vh", background: colors.bg }}>
      <div style={{ maxWidth: 640, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: `${spacing.md}px ${spacing.lg}px` }}>
          <button data-testid="back-button" onClick={() => navigate(-1)} style={plain}><ChevronLeft size={26} color={colors.ink} /></button>
          <div style={{ ...type.serif, fontSize: 20, color: colors.ink }}>{editing ? "Modifica piatto" : "Nuovo piatto"}</div>
          <button data-testid="save-item-button" onClick={save} disabled={saving || !nome.trim()} style={{ background: "none", border: "none", ...type.sansSemi, fontSize: 16, color: colors.accent, opacity: !nome.trim() ? 0.4 : 1 }}>{saving ? "…" : "Salva"}</button>
        </div>

        <div style={{ padding: spacing.lg, display: "flex", flexDirection: "column", gap: spacing.md }}>
          <button data-testid="pick-photo-button" onClick={() => fileRef.current?.click()} style={{ height: 180, borderRadius: radius.lg, background: colors.surface, border: `1px solid ${colors.line}`, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", position: "relative" }}>
            {uploading ? <span style={{ ...type.sans, color: colors.accent }}>Caricamento…</span> : foto.foto_url ? <img src={mediaUrl(foto.foto_url)} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: spacing.xs }}><Camera size={26} color={colors.inkSoft} /><span style={{ ...type.sansMedium, fontSize: 14, color: colors.inkSoft }}>Aggiungi foto</span></div>}
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={onPick} style={{ display: "none" }} />

          <Field label="Nome" value={nome} onChange={setNome} testID="item-nome" />
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={lbl}>Descrizione</label>
            <textarea data-testid="item-desc" style={area} value={descrizione} onChange={(e) => setDescrizione(e.target.value)} />
          </div>
          <div style={{ display: "flex", gap: spacing.md }}>
            <div style={{ flex: 1 }}><Field label="Prezzo base (€)" value={prezzo} onChange={setPrezzo} testID="item-prezzo" /></div>
            <div style={{ flex: 1 }}><Field label="Prezzo cena (€)" value={cena} onChange={setCena} testID="item-cena" /></div>
          </div>
          <Field label="Tempo prep (min)" value={tempo} onChange={setTempo} testID="item-tempo" />

          {editing ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: colors.surface, border: `1px solid ${colors.line}`, borderRadius: radius.md, padding: spacing.md }}>
              <span style={lbl}>Disponibile</span>
              <button data-testid="item-disponibile" onClick={() => setDisponibile((v) => !v)} role="switch" aria-checked={disponibile} style={{ width: 48, height: 28, borderRadius: 14, border: "none", background: disponibile ? colors.success : colors.lineStrong, position: "relative", padding: 0 }}>
                <span style={{ position: "absolute", top: 3, left: disponibile ? 23 : 3, width: 22, height: 22, borderRadius: 11, background: "#fff", transition: "left .18s ease" }} />
              </button>
            </div>
          ) : null}

          <label style={lbl}>Tag</label>
          <div style={chips}>
            {Object.keys(TAG_LABELS).map((t) => { const on = tag.has(t); return <button key={t} data-testid={`tag-${t}`} onClick={() => toggleSet(setTag, t)} style={{ ...chip, background: on ? colors.ink : colors.surface, borderColor: on ? colors.ink : colors.line, color: on ? "#fff" : colors.inkSoft }}>{TAG_LABELS[t]}</button>; })}
          </div>

          <label style={lbl}>Allergeni (14 UE)</label>
          <div style={chips}>
            {Object.keys(ALLERGEN_LABELS).map((a) => { const on = allergeni.has(a); return <button key={a} data-testid={`allergen-${a}`} onClick={() => toggleSet(setAllergeni, a)} style={{ ...chip, background: on ? colors.danger : colors.surface, borderColor: on ? colors.danger : colors.line, color: on ? "#fff" : colors.inkSoft }}>{ALLERGEN_LABELS[a]}</button>; })}
          </div>

          {editing ? (
            <button data-testid="delete-item-button" onClick={del} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: spacing.sm, height: 48, borderRadius: radius.md, border: `1px solid ${colors.danger}`, background: "transparent", marginTop: spacing.md, ...type.sansSemi, fontSize: 15, color: colors.danger }}>
              <Trash2 size={16} color={colors.danger} />Elimina piatto
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testID }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={lbl}>{label}</label>
      <input data-testid={testID} style={input} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

const plain = { background: "none", border: "none", display: "flex", padding: 0 };
const lbl = { ...type.sansMedium, fontSize: 14, color: colors.ink };
const input = { background: colors.surface, border: `1px solid ${colors.line}`, borderRadius: radius.md, height: 48, padding: `0 ${spacing.md}px`, ...type.sans, fontSize: 15, color: colors.ink };
const area = { background: colors.surface, border: `1px solid ${colors.line}`, borderRadius: radius.md, minHeight: 70, padding: spacing.md, ...type.sans, fontSize: 15, color: colors.ink, resize: "vertical" };
const chips = { display: "flex", flexWrap: "wrap", gap: spacing.sm };
const chip = { borderWidth: 1, borderStyle: "solid", borderRadius: radius.pill, padding: `8px ${spacing.md}px`, ...type.sansMedium, fontSize: 13 };
